import { __awaiter } from "tslib";
import { Device } from "@auvious/compatibility";
import { Events, localStore } from "@auvious/utils";
import debug from "debug";
const log = debug("auvious:devices-manager");
const defaultLabelName = {
    audioinput: "Microphone",
    audiooutput: "Speaker",
    videoinput: "Camera",
};
function capture(f) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield f();
            return null;
        }
        catch (ex) {
            return ex;
        }
    });
}
class Devices {
    constructor() {
        this.events = new Events();
        /** when false it means user has not yet been prompted or user has blocked access */
        this.permissions = {
            audioinput: false,
            videoinput: false,
            audiooutput: false,
        };
        this.has = {
            audioinput: 0,
            videoinput: 0,
            audiooutput: 0,
        };
        this.activeSpeaker = null;
        this.constraints = {};
        /** all devices by groupdId-device pair */
        this.groups = {};
        /** all devices categorized by kind */
        this.devices = {
            audioinput: {},
            videoinput: {},
            audiooutput: {},
        };
        this._preferredConstraints = {};
        this.preferred = {
            audioinput: null,
            videoinput: null,
            audiooutput: null,
        };
        this.hasBeenSetup = false;
        this.isRefreshing = false;
        this.sinkSources = new Set();
        this.pendingPermissionTimeout = -1;
        this.permissionStatus = { camera: null, microphone: null };
        this.onDeviceChange = () => this.refreshDevices();
        this.onPermissionsChanged = () => this.updatePermissions();
        try {
            const stored = JSON.parse(localStore.getItem(Devices.storageKey) || "{}");
            if (typeof stored === "object") {
                log("found preferred devices", stored);
                this.preferred = stored;
            }
        }
        catch (_a) { }
    }
    get preferredConstraints() {
        delete this._preferredConstraints.video;
        delete this._preferredConstraints.audio;
        if (this.preferred.audioinput) {
            this._preferredConstraints.audio = {
                deviceId: this.preferred.audioinput.deviceId,
            };
        }
        if (this.preferred.videoinput) {
            this._preferredConstraints.video = {
                deviceId: this.preferred.videoinput.deviceId,
            };
        }
        return this._preferredConstraints;
    }
    getDeviceList(kind) {
        return kind
            ? Object.values(this.devices[kind])
            : [
                ...Object.values(this.devices.audioinput),
                ...Object.values(this.devices.videoinput),
                ...Object.values(this.devices.audiooutput),
            ];
    }
    /** empty string to reset to default, null to use user preferred */
    setSpeaker(id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Device.isSpeakerSelectionSupported) {
                return;
            }
            if (id === null && this.preferred.audiooutput) {
                id = this.preferred.audiooutput.deviceId;
            }
            else if (id === null) {
                return;
            }
            for (const elm of this.sinkSources) {
                try {
                    yield this.setSinkId(id, elm);
                }
                catch (_a) { }
            }
        });
    }
    /** sync source with active set speaker, if none, then preferred output, if none do nothing */
    syncSpeaker(elm) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!Device.isSpeakerSelectionSupported) {
                return;
            }
            this.sinkSources.add(elm);
            yield this.setSinkId(((_a = this.activeSpeaker) === null || _a === void 0 ? void 0 : _a.deviceId) ||
                ((_b = this.preferred.audiooutput) === null || _b === void 0 ? void 0 : _b.deviceId) ||
                "", elm);
        });
    }
    /** remove from internal list */
    desyncSpeaker(elm) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Device.isSpeakerSelectionSupported) {
                return;
            }
            yield new Promise((resolve) => setTimeout(resolve, 0));
            this.sinkSources.delete(elm);
        });
    }
    /** better use other public methods */
    setSinkId(id, elm) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = elm.srcObject) === null || _a === void 0 ? void 0 : _a.getAudioTracks()[0]) && !((_b = elm.src) === null || _b === void 0 ? void 0 : _b.length)) {
                return;
            }
            const backup = elm.sinkId;
            const ex = yield capture(() => elm.setSinkId(id));
            if (!ex) {
                this.activeSpeaker = this.getById(id);
            }
            else if (ex instanceof DOMException && !this.isRefreshing) {
                log("failed to set speaker for", id);
                this.refreshDevices();
                throw ex;
            }
            else if (!(yield capture(() => elm.setSinkId(backup)))) {
                log("failed to set speaker, reverted to", backup);
                this.activeSpeaker = this.getById(backup);
            }
            else if (!(yield capture(() => elm.setSinkId("")))) {
                log("failed to set speaker, returned to default");
                this.activeSpeaker = null;
            }
            log("speaker is set to", this.activeSpeaker);
        });
    }
    /**
     * partial kind to device-id pairs, of which one, if null, is unset
     */
    savePreferred(kinds) {
        this.preferred.audioinput = kinds.audioinput
            ? this.devices.audioinput[kinds.audioinput]
            : null;
        this.preferred.videoinput = kinds.videoinput
            ? this.devices.videoinput[kinds.videoinput]
            : null;
        this.preferred.audiooutput = kinds.audiooutput
            ? this.devices.audiooutput[kinds.audiooutput]
            : null;
        localStore.setItem(Devices.storageKey, JSON.stringify(this.preferred));
    }
    getByLabel(label, kind) {
        const lookup = (devs) => devs
            ? Object.values(devs).find((device) => device.label === label)
            : null;
        return ((kind
            ? lookup(this.devices[kind])
            : lookup(this.devices.audioinput) ||
                lookup(this.devices.videoinput) ||
                lookup(this.devices.audiooutput)) || null);
    }
    getById(id, kind) {
        return ((kind
            ? this.devices[kind][id]
            : this.devices.audioinput[id] ||
                this.devices.videoinput[id] ||
                this.devices.audiooutput[id]) || null);
    }
    /** register native event handlers and enumerate devices */
    setup(listeners) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.hasBeenSetup) {
                return;
            }
            if (listeners) {
                this.events.subscribe(listeners);
            }
            navigator.mediaDevices.addEventListener("devicechange", this.onDeviceChange);
            if (navigator.permissions) {
                try {
                    this.permissionStatus.microphone = yield navigator.permissions.query({
                        // @ts-ignore
                        name: "microphone",
                    });
                    this.permissions.audiooutput = this.permissions.audioinput =
                        this.permissionStatus.microphone.state === "granted";
                    this.permissionStatus.microphone.addEventListener("change", this.onPermissionsChanged);
                }
                catch (_a) { }
                try {
                    this.permissionStatus.camera = yield navigator.permissions.query({
                        // @ts-ignore
                        name: "camera",
                    });
                    this.permissions.videoinput =
                        this.permissionStatus.camera.state === "granted";
                    this.permissionStatus.camera.addEventListener("change", this.onPermissionsChanged);
                }
                catch (_b) { }
                log("permissions api is supported", Object.assign({}, this.permissions));
            }
            else {
                log("permissions api is not supported");
            }
            yield this.refreshDevices();
            this.hasBeenSetup = true;
        });
    }
    /** clear event listerners and state */
    clear() {
        navigator.mediaDevices.removeEventListener("devicechange", this.onDeviceChange);
        if (this.permissionStatus.camera) {
            this.permissionStatus.camera.removeEventListener("change", this.onPermissionsChanged);
        }
        if (this.permissionStatus.microphone) {
            this.permissionStatus.microphone.removeEventListener("change", this.onPermissionsChanged);
        }
        this.groups = {};
        this.devices = {
            audioinput: {},
            videoinput: {},
            audiooutput: {},
        };
        this.hasBeenSetup = false;
        this.events.clear();
    }
    /** to be merged with constraints of getUserMedia */
    setConstraints(key, constraints) {
        if (key === "auvious.asr" && Device.isSafari) {
            return;
        }
        this.constraints[key] = constraints;
    }
    unsetConstraints(key) {
        delete this.constraints[key];
    }
    /** enumerate and categorize devices, might trigger added, updated, removed events */
    refreshDevices() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.isRefreshing = true;
            const devices = yield navigator.mediaDevices.enumerateDevices();
            log("enumerated devices", devices);
            const updated = [];
            const added = [];
            const toRemove = {
                audioinput: Object.assign({}, this.devices.audioinput),
                videoinput: Object.assign({}, this.devices.videoinput),
                audiooutput: Object.assign({}, this.devices.audiooutput),
            };
            const newGroups = {};
            const newDevices = {
                audioinput: {},
                videoinput: {},
                audiooutput: {},
            };
            this.has = {
                audioinput: 0,
                videoinput: 0,
                audiooutput: 0,
            };
            // keep full count of devices with no permissions as well
            const mediaCount = {
                audioinput: 0,
                videoinput: 0,
                audiooutput: 0,
            };
            const permissions = {};
            for (const dev of devices) {
                const device = dev.toJSON();
                mediaCount[device.kind]++;
                if (device.deviceId && device.deviceId !== "default") {
                    this.has[device.kind]++;
                }
                // one less to remove
                delete toRemove[device.kind][device.deviceId];
                permissions[device.kind] = !!(device.label && device.deviceId);
                if (device.deviceId) {
                    if (newDevices[device.kind][device.deviceId]) {
                        continue;
                    }
                    // label has changed (firefox)
                    if (device.deviceId in this.devices[device.kind] &&
                        device.label &&
                        device.label !== this.devices[device.kind][device.deviceId].label) {
                        updated.push(device);
                    }
                    else if (!(device.deviceId in this.devices[device.kind])) {
                        added.push(device);
                    }
                    if (!device.label) {
                        // @ts-ignore
                        device.label = `${defaultLabelName[device.kind]} #${Object.keys(newDevices[device.kind]).length + 1}`;
                    }
                    newDevices[device.kind][device.deviceId] = device;
                    if (device.groupId) {
                        if (newGroups[device.groupId]) {
                            newGroups[device.groupId].push(device);
                        }
                        else {
                            newGroups[device.groupId] = [device];
                        }
                    }
                }
            }
            // when no permissions are granted, at least count one if it exists
            this.has.audioinput =
                this.has.audioinput || (mediaCount.audioinput ? 1 : 0);
            this.has.videoinput =
                this.has.videoinput || (mediaCount.videoinput ? 1 : 0);
            this.has.audiooutput =
                this.has.audiooutput || (mediaCount.audiooutput ? 1 : 0);
            this.devices = newDevices;
            this.groups = newGroups;
            if (Object.keys(permissions).length) {
                this.updatePermissions(permissions);
            }
            this.updatePreferred();
            // make sure active speaker is valid
            let speaker = "";
            if (this.activeSpeaker !== null &&
                this.devices.audiooutput[this.activeSpeaker.deviceId]) {
                speaker = this.activeSpeaker.deviceId;
            }
            else if (this.preferred.audiooutput &&
                this.devices.audiooutput[this.preferred.audiooutput.deviceId]) {
                const dev = this.getByLabel(this.preferred.audiooutput.label, "audiooutput");
                if (dev) {
                    speaker = this.preferred.audiooutput.deviceId;
                }
            }
            if (speaker !== ((_a = this.activeSpeaker) === null || _a === void 0 ? void 0 : _a.deviceId)) {
                yield this.setSpeaker(speaker);
            }
            // on first run, this is empty
            const removed = [
                ...Object.values(toRemove.audioinput),
                ...Object.values(toRemove.videoinput),
                ...Object.values(toRemove.audiooutput),
            ];
            if (removed.length) {
                this.events.emit("removed", removed);
            }
            if (updated.length) {
                this.events.emit("updated", updated);
            }
            if (added.length) {
                this.events.emit("added", added);
            }
            if (removed.length || updated.length || added.length) {
                this.events.emit("changed");
            }
            this.isRefreshing = false;
        });
    }
    updatePreferred() {
        let updated = false;
        for (const kind of ["audioinput", "videoinput", "audiooutput"]) {
            if (this.preferred[kind] &&
                !(this.preferred[kind].deviceId in this.devices[kind])) {
                const dev = this.getByLabel(kind, this.preferred[kind].label);
                if (dev) {
                    updated = true;
                    this.preferred[kind] = dev;
                }
            }
        }
        if (updated) {
            localStore.setItem(Devices.storageKey, JSON.stringify(this.preferred));
        }
    }
    updatePermissions(update) {
        // events from permissions api have higher priority
        if (!update && this.pendingPermissionTimeout !== -1) {
            window.clearTimeout(this.pendingPermissionTimeout);
            this.pendingPermissionTimeout = -1;
        }
        if (this.pendingPermissionTimeout === -1) {
            this.pendingPermissionTimeout = window.setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                this.pendingPermissionTimeout = -1;
                const event = {};
                let refreshDevices = false;
                if (!update) {
                    refreshDevices = true;
                    update = {
                        audioinput: ((_a = this.permissionStatus.microphone) === null || _a === void 0 ? void 0 : _a.state) === "granted",
                        audiooutput: ((_b = this.permissionStatus.microphone) === null || _b === void 0 ? void 0 : _b.state) === "granted",
                        videoinput: ((_c = this.permissionStatus.camera) === null || _c === void 0 ? void 0 : _c.state) === "granted",
                    };
                }
                else if (update.audioinput && !update.audiooutput) {
                    update.audiooutput = update.audioinput;
                }
                if (update.videoinput !== undefined &&
                    update.videoinput !== this.permissions.videoinput) {
                    event.videoinput = this.permissions.videoinput = update.videoinput;
                }
                if (update.audioinput !== undefined &&
                    update.audioinput !== this.permissions.audioinput) {
                    event.audioinput = this.permissions.audioinput = update.audioinput;
                }
                if (update.audiooutput !== undefined &&
                    update.audiooutput !== this.permissions.audiooutput) {
                    event.audiooutput = this.permissions.audiooutput = update.audiooutput;
                }
                if (Object.keys(event).length) {
                    log("permissions changed", event);
                    this.events.emit("permissions", event);
                }
                if (refreshDevices) {
                    yield this.refreshDevices();
                }
            }), 50);
        }
    }
}
Devices.storageKey = "auvious.devices.preferences";
export const MediaDevices = new Devices();
